export enum FeatureType {
  Basic = 'Basic',
  Experience = 'Experience'
}

export enum ProductLine {
  Marketplace = 'Marketplace',
  Health = 'Health'
}

export enum PricingFrequency {
  Once = 'Once',
  Startup = 'Startup',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  SemiAnnual = 'SemiAnnual',
  Annual = 'Annual'
}

export enum UsageFrequency {
  Static = 'Static',
  Unlimited = 'Unlimited',
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  SemiAnnual = 'SemiAnnual',
  Annual = 'Annual'
}

export enum DiscountType {
  Percentage = 'Percentage',
  Flat = 'Flat'
}

export interface CreatePackageRequestV1 {
  name: string;
  description?: string;
  categoryCode: string;
  shorthandCode: string;
  baselinePrice: number;
  productLine?: ProductLine;
  oneTimeFee?: number;
  pricingFrequency?: PricingFrequency;
  isActive: boolean;
  optionIds?: string[];
}

export interface CreateOptionRequestV1 {
  name: string;
  description?: string;
  categoryCode: string;
  shorthandCode: string;
  baselinePrice: number;
  productLine?: ProductLine;
  oneTimeFee?: number;
  pricingFrequency?: PricingFrequency;
  isRequired?: boolean;
  isDefault: boolean;
  isActive: boolean;
  featureIds?: string[];
}

export interface CreateFeatureRequestV1 {
  name: string;
  description?: string;
  categoryCode: string;
  shorthandCode: string;
  featureType: FeatureType;
  baselinePrice: number;
  productLine?: ProductLine;
  oneTimeFee?: number;
  inventory?: number;
  pricingFrequency?: PricingFrequency;
  usageFrequency?: UsageFrequency;
  discountRate?: number;
  discountType?: DiscountType;
  isGroupLevel?: boolean;
  isActive: boolean;
}

export interface FeatureCatalogFilterV1 {
  isGroupLevel?: boolean;
  shorthandCode?: string;
  productLine?: ProductLine;
  categoryCode?: string;
  version?: number;
  featureType?: FeatureType;
  featureCode?: string;
}

export interface PackageResponseV1 {
  packageId: string;
  name: string;
  description?: string;
  packageCode?: string;
  categoryCode: string;
  shorthandCode: string;
  productLine: ProductLine;
  baselinePrice: number;
  oneTimeFee?: number;
  pricingFrequency: PricingFrequency;
  isActive: boolean;
  createdOn: Date | string;
  modifiedOn: Date | string;
  archivedOn?: Date | string | null;
  optionIds?: string[];
  optionCount?: number;
}

export interface OptionResponseV1 {
  optionId: string;
  name: string;
  description?: string;
  optionCode: string;
  categoryCode: string;
  shorthandCode: string;
  productLine: ProductLine;
  isRequired: boolean;
  isDefault: boolean;
  baselinePrice: number;
  oneTimeFee?: number;
  pricingFrequency: PricingFrequency;
  isActive: boolean;
  createdOn: Date | string;
  modifiedOn: Date | string;
  archivedOn?: Date | string | null;
  featureIds?: string[];
  featureCount?: number;
}

export interface FeatureResponseV1 {
  featureId: string;
  name: string;
  description?: string;
  featureCode: string;
  categoryCode: string;
  shorthandCode: string;
  productLine: ProductLine;
  featureType: FeatureType;
  baselinePrice: number;
  oneTimeFee?: number;
  inventory: number;
  pricingFrequency: PricingFrequency;
  usageFrequency: UsageFrequency;
  discountRate: number;
  discountType: DiscountType;
  isGroupLevel: boolean;
  isActive: boolean;
  createdOn: Date | string;
  modifiedOn: Date | string;
  archivedOn?: Date | string | null;
}

export interface UserMembershipDetail {
  membershipId: string;
  packageId: string;
  packageName: string;
  optionId: string;
  optionName: string;
  assignedOn: Date | string;
  cancelledOn?: Date | string | null;
  isActive: boolean;
}

export interface UserMembershipSummary {
  userId: string;
  email: string;
  name: string;
  activeMembershipCount: number;
  memberships: UserMembershipDetail[];
}

export interface AccountMembershipResponseV1 {
  accountId: string;
  totalUsers: number;
  totalActiveMemberships: number;

  /**
   * Access Management sends this as `memberships`, and always has. Typed here as `users` it bound as
   * undefined on every response, so the account's membership panel rendered its empty state for an
   * account that had memberships, and the user panel found none for a user who held one.
   */
  memberships: UserMembershipSummary[];
}

export enum MembershipStatus {
  Pending = 'Pending',
  Active = 'Active',
  Cancelled = 'Cancelled',
  Suspended = 'Suspended'
}

export interface MembershipFeatureV1 {
  featureId: string;
  featureCode: string;
  featureName: string;
  optionId: string;
  optionCode: string;
  optionName: string;
  isActive: boolean;
}

export interface MembershipDetailV1 {
  membershipId: string;
  groupId: string;
  userId?: string;
  packageId: string;
  packageName: string;
  packageCode?: string;
  status: MembershipStatus;
  /** PaymentsEngine's per-seat subscription item, not an order. */
  subscriptionItemId?: string;
  assignedOn: string;
  /** AccessManagement's own name for it. It was read as allocatedOn, which was never on the wire. */
  activatedOn?: string;
  cancelledOn?: string;
  isActive: boolean;
  features: MembershipFeatureV1[];
}

export interface MembershipSummaryV1 {
  userId: string;
  email: string;
  name?: string;
  memberships: MembershipDetailV1[];
  activeMembershipCount: number;
}

export interface GroupMembershipResponseV1 {
  groupId: string;
  memberships: MembershipSummaryV1[];
  totalUsers: number;
  totalActiveMemberships: number;
}

export interface PackageOptionResponseV1 {
  packageOptionId: string;
  optionId: string;
  packageId: string;
  createdOn: Date | string;
  modifiedOn: Date | string;
  archivedOn?: Date | string | null;
}

export interface OptionFeatureResponseV1 {
  optionFeatureId: string;
  optionId: string;
  featureId: string;
  createdOn: Date | string;
}

export interface PackageDisplay extends PackageResponseV1 {
  options?: OptionResponseV1[];
  packageOptionIds?: string[];
}

export interface OptionDisplay extends OptionResponseV1 {
  features?: FeatureResponseV1[];
  optionFeatureIds?: string[];
  packages?: PackageResponseV1[];
}

export interface FeatureDisplay extends FeatureResponseV1 {
  options?: OptionResponseV1[];
}

export enum CatalogStatus {
  Active = 'Active',
  Inactive = 'Inactive',
  Deprecated = 'Deprecated'
}

export interface PackageSubscribersV1 {
  packageId: string;
  name: string;
  packageCode?: string;
  productLine: ProductLine;
  status: CatalogStatus;
  isActive: boolean;
  level: number;
  version: number;
  baselinePrice: number;
  activeMemberships: number;
  pendingMemberships: number;
  cancelledMemberships: number;
  totalMemberships: number;
}

export interface PackageProductLineSummaryV1 {
  productLine: ProductLine;
  packageCount: number;
  activeSubscribers: number;
  committedSubscribers: number;
  pendingMemberships: number;
  packages: PackageSubscribersV1[];
}

export interface PackagesSummaryV1 {
  generatedAt: string;
  totalActivePackages: number;
  totalActiveSubscribers: number;
  totalCommittedSubscribers: number;
  totalPendingMemberships: number;
  totalCancelledMemberships: number;
  productLines: PackageProductLineSummaryV1[];
}
